import dayjs from 'dayjs';
import {
    ProcessFieldSelect,
    ServiceFieldTableRowDto,
    ServiceFieldTableValueDto,
    ServiceProcessFieldTableColumnVM,
    TableFieldSelectionRecordVM,
} from '@shared/types/process';
import { generateUUID } from '@shared/util/generateUUID';
import { getLangText } from '@shared/util/textGetters';
import { Lng } from '@shared/types/types';
import { normalizeColumnOptions } from './tableFieldColumnUtils';

export const GLOBAL_SEARCH_FILTER_KEY = '__tableFieldSearch';

export const EMPTY_TABLE_ROW_SELECTION_RECORD_ID =
    '00000000-0000-0000-0000-000000000001';

export function createEmptySelectionRecord(): TableFieldSelectionRecordVM {
    return {
        recordId: EMPTY_TABLE_ROW_SELECTION_RECORD_ID,
        displayLines: null,
        prefilledCells: null,
    };
}

export function isEmptySelectionRecord(
    record: TableFieldSelectionRecordVM,
): boolean {
    return record.recordId === EMPTY_TABLE_ROW_SELECTION_RECORD_ID;
}

export function parseTableFieldValue(
    raw: unknown,
): ServiceFieldTableValueDto {
    if (raw == null || raw === '') {
        return { rows: [] };
    }

    let parsed: unknown = raw;

    if (typeof raw === 'string') {
        try {
            parsed = JSON.parse(raw);
        } catch {
            return { rows: [] };
        }
    }

    if (Array.isArray(parsed)) {
        return { rows: [] };
    }

    if (
        typeof parsed === 'object' &&
        parsed !== null &&
        'rows' in parsed &&
        Array.isArray((parsed as ServiceFieldTableValueDto).rows)
    ) {
        return {
            rows: (parsed as ServiceFieldTableValueDto).rows.map(
                normalizeRow,
            ),
        };
    }

    return { rows: [] };
}

function normalizeRow(row: ServiceFieldTableRowDto): ServiceFieldTableRowDto {
    return {
        rowId: row.rowId || generateUUID(),
        sourceRecordId: row.sourceRecordId ?? null,
        cells: row.cells ?? {},
    };
}

export function serializeTableRows(
    rows: ServiceFieldTableRowDto[],
): ServiceFieldTableRowDto[] {
    return rows.map((row) => ({
        rowId: row.rowId,
        sourceRecordId: row.sourceRecordId,
        cells: row.cells ?? {},
    }));
}

export function formatDisplayLines(lines: string[] | null | undefined): string {
    return (lines ?? []).filter(Boolean).join(' · ');
}

export function formatCellDisplayValue(value: unknown): string {
    if (value == null || value === '') {
        return '';
    }

    if (typeof value === 'boolean') {
        return value ? 'true' : 'false';
    }

    if (typeof value === 'object') {
        try {
            return JSON.stringify(value);
        } catch {
            return '';
        }
    }

    return String(value);
}

export function formatTableCellDisplayValue(
    column: ServiceProcessFieldTableColumnVM,
    value: unknown,
    lang: Lng,
): string {
    if (value == null || value === '') {
        return '';
    }

    const typeId = column.manualFieldTypeId;
    const options = normalizeColumnOptions(column.options);

    if (typeId === 'Checkbox') {
        const checked = value === true || value === 'true';
        return checked ? 'true' : 'false';
    }

    if (typeId === 'Select' || typeId === 'SelectIdp') {
        const selectOptions = (
            options as ProcessFieldSelect['options'] | null
        )?.options;
        const stringValue = String(value);
        const match = selectOptions?.find(
            (item) =>
                String(item.id ?? item.value ?? '') === stringValue ||
                String(item.value ?? '') === stringValue,
        );

        if (match) {
            return getLangText(match.label, lang, stringValue);
        }
    }

    if (typeId === 'Date' || typeId === 'DateRange') {
        const format =
            (options as { format?: string } | null)?.format ??
            'DD.MM.YYYY';
        const parsed = dayjs(String(value));
        if (parsed.isValid()) {
            return parsed.format(format);
        }
    }

    return formatCellDisplayValue(value);
}

export function getColumnTitle(
    column: ServiceProcessFieldTableColumnVM,
    lang: Lng,
): string {
    return getLangText(column.title, lang, '');
}

export function getUsedSourceRecordIds(
    rows: ServiceFieldTableRowDto[],
): string[] {
    return rows
        .map((row) => row.sourceRecordId)
        .filter((id): id is string => Boolean(id));
}

export function createRowFromSelection(
    record: TableFieldSelectionRecordVM,
): ServiceFieldTableRowDto {
    if (isEmptySelectionRecord(record)) {
        return {
            rowId: generateUUID(),
            sourceRecordId: null,
            cells: {},
        };
    }

    return {
        rowId: generateUUID(),
        sourceRecordId: record.recordId,
        cells: { ...(record.prefilledCells ?? {}) },
    };
}

export type TableRowsQueryState = {
    search?: string;
    columnFilters?: Record<string, unknown>;
    sortColumnId?: string;
    sortDescending?: boolean;
    page: number;
    pageSize: number;
};

function cellMatchesFilter(
    cellValue: unknown,
    filterValue: unknown,
): boolean {
    if (filterValue == null || filterValue === '') {
        return true;
    }

    if (Array.isArray(filterValue) && filterValue.length === 2) {
        const [fromRaw, toRaw] = filterValue;
        const hasFrom =
            fromRaw != null && fromRaw !== '' && String(fromRaw).trim() !== '';
        const hasTo =
            toRaw != null && toRaw !== '' && String(toRaw).trim() !== '';

        if (!hasFrom && !hasTo) {
            return true;
        }

        const cellText = formatCellDisplayValue(cellValue);
        if (!cellText) {
            return false;
        }

        const cellDate = dayjs(cellText, ['DD.MM.YYYY', 'YYYY-MM-DD'], true);
        if (!cellDate.isValid()) {
            return cellText.toLowerCase().includes(String(fromRaw ?? toRaw).toLowerCase());
        }

        if (hasFrom) {
            const from = dayjs(fromRaw);
            if (from.isValid() && cellDate.isBefore(from, 'day')) {
                return false;
            }
        }

        if (hasTo) {
            const to = dayjs(toRaw);
            if (to.isValid() && cellDate.isAfter(to, 'day')) {
                return false;
            }
        }

        return true;
    }

    if (Array.isArray(filterValue)) {
        const cellText = formatCellDisplayValue(cellValue).toLowerCase();
        return filterValue.some((item) =>
            cellText.includes(String(item).toLowerCase()),
        );
    }

    const needle = String(filterValue).trim().toLowerCase();
    if (!needle) {
        return true;
    }

    return formatCellDisplayValue(cellValue).toLowerCase().includes(needle);
}

export function queryTableRows(
    rows: ServiceFieldTableRowDto[],
    columns: ServiceProcessFieldTableColumnVM[],
    state: TableRowsQueryState,
): { itemList: ServiceFieldTableRowDto[]; itemCount: number } {
    let result = [...rows];

    const search = state.search?.trim().toLowerCase();
    if (search) {
        result = result.filter((row) =>
            columns.some((column) =>
                formatCellDisplayValue(row.cells?.[column.id])
                    .toLowerCase()
                    .includes(search),
            ),
        );
    }

    const columnFilters = state.columnFilters ?? {};
    Object.entries(columnFilters).forEach(([columnId, filterValue]) => {
        if (filterValue == null || filterValue === '') {
            return;
        }

        if (Array.isArray(filterValue) && filterValue.length === 0) {
            return;
        }

        result = result.filter((row) =>
            cellMatchesFilter(row.cells?.[columnId], filterValue),
        );
    });

    if (state.sortColumnId) {
        const sortColumnId = state.sortColumnId;
        const direction = state.sortDescending ? -1 : 1;

        result.sort((left, right) => {
            const leftValue = formatCellDisplayValue(
                left.cells?.[sortColumnId],
            );
            const rightValue = formatCellDisplayValue(
                right.cells?.[sortColumnId],
            );

            return (
                leftValue.localeCompare(rightValue, undefined, {
                    numeric: true,
                    sensitivity: 'base',
                }) * direction
            );
        });
    }

    const itemCount = result.length;
    const start = (state.page - 1) * state.pageSize;
    const itemList = result.slice(start, start + state.pageSize);

    return { itemList, itemCount };
}

export function isTableColumnReadOnly(
    column: ServiceProcessFieldTableColumnVM,
): boolean {
    return (
        column.fillSourceId === 'SourceField' || column.isReadOnly === true
    );
}

export function isColumnEditable(
    column: ServiceProcessFieldTableColumnVM,
): boolean {
    return !isTableColumnReadOnly(column);
}

export function getLastPage(totalRows: number, pageSize: number): number {
    if (totalRows <= 0) {
        return 1;
    }

    return Math.ceil(totalRows / pageSize);
}

export function selectionRecordToOption(record: TableFieldSelectionRecordVM) {
    return {
        label: formatDisplayLines(record.displayLines),
        value: record.recordId,
        record,
    };
}
